import type { ApolloClient } from '@apollo/client';
import { orderBy } from 'lodash-es';
import deburr from 'lodash/deburr';
import escapeRegExp from 'lodash/escapeRegExp';
import { keep } from '$lib/utils/filterNotNull';
import { pipe } from '$lib/utils/pipe';
import type { lazy } from '$lib/utils/types';
import {
    type SelectCategory_LoadOptionQueryQuery,
    SelectCategory_LoadOptionsQueryDocument,
    SelectCategory_LoadOptionQueryDocument,
} from '$gql/graphql';

type Option = SelectCategory_LoadOptionQueryQuery['category'];

export async function loadOptions<A extends ApolloClient<unknown>>(
    apollo: lazy<A>,
    q: string,
    leaf: boolean | null,
    parentId: number | null,
): Promise<Option[]> {
    const response = await apollo().query({
        query: SelectCategory_LoadOptionsQueryDocument,
    });
    const categories = response.data?.categories ?? [];

    const regex = new RegExp(deburr(q).split(' ').map(escapeRegExp).join('.*'), 'ig');

    return pipe(
        categories,
        (_) =>
            keep(_, (c) => {
                if (parentId != null) {
                    return c.parent?.id === parentId;
                }

                return true;
            }),
        (_) =>
            keep(_, (c) => {
                return regex.test(deburr(c.name));
            }),
        (_) =>
            keep(_, (c) => {
                if (leaf === null) {
                    return true;
                } else {
                    return c.hasChildren !== leaf;
                }
            }),
        (_) =>
            orderBy(
                _,
                [
                    (c) =>
                        deburr(c.name.toLocaleLowerCase()).startsWith(
                            deburr(q.toLocaleLowerCase()),
                        )
                            ? 1
                            : 2,
                    (c) => deburr(c.name.toLocaleLowerCase()),
                ],
                ['asc', 'asc'],
            ),
    );
}

export function optionToValue(option: Option): number {
    return option.id;
}

export async function valueToOption<A extends ApolloClient<unknown>>(
    apollo: lazy<A>,
    value: number,
): Promise<Option | null> {
    return (
        (
            await apollo().query({
                query: SelectCategory_LoadOptionQueryDocument,
                variables: { id: value },
            })
        ).data?.category ?? null
    );
}

export function valueToId(value: number): number {
    return value;
}
